import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  MenuItem,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { memo, useState } from "react";
import { flushSync } from "react-dom";
import { DragDropContext, Draggable, DropResult, Droppable } from "react-beautiful-dnd";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { t } from "i18next";
import { ExpandMoreIcon } from "common-lib";
import { DefectOptionItem, InitialOptionValue } from "@types";
import { resolveParamLimits } from "../../utils/defectUtils";

// ponytail: common_ui_lib의 AIOptionPanel과 같은 UI지만 저장 버튼 없이 바로 onChange로 올린다.
// AIOptionPanel에 onChange prop이 생기면 이 컴포넌트는 그걸로 대체
interface DefectOptionPanelProps {
  options: Record<string, DefectOptionItem>;
  initialOptions: Record<string, InitialOptionValue>;
  onChange: (optionKey: string, paramKey: string, value: any) => void;
  onReorder?: (from: number, to: number) => void; // 있으면 드래그로 순서 변경 가능
  onSolo?: (optionKey: string) => void; // 우클릭 → 이 옵션만 켜기
}

const NUM_FIELD_SX = {
  width: 72,
  "& input": { textAlign: "center", padding: "4px 6px" },
};

const clamp = (val: string, min: number, max: number, isInt: boolean) => {
  const v = Math.min(max, Math.max(min, Number(val)));
  return isInt ? Math.round(v) : Number(v.toFixed(4));
};

// 숫자 입력칸: 입력 중에는 쓰는 그대로 두고, 포커스가 빠지거나 Enter일 때만 반영(onCommit에서 범위 보정)
// 키 입력마다 보정하면 "255 → 50"처럼 지우고 다시 쓰는 중간값(빈칸, 5)이 최소값으로 튀어 원하는 값을 쓸 수 없다
const NumField = ({
  value,
  onCommit,
  disabled,
  inputProps,
}: {
  value: any;
  onCommit: (raw: string) => void;
  disabled?: boolean;
  inputProps?: Record<string, any>;
}) => {
  const [text, setText] = useState<string | null>(null); // null = 편집 중 아님 → value 그대로 표시
  const commit = () => {
    if (text !== null && text.trim() !== "" && !Number.isNaN(Number(text)) && text !== String(value)) onCommit(text);
    setText(null); // 빈칸/숫자 아님이면 원래 값으로 되돌아감
  };
  return (
    <TextField
      size="small"
      type="number"
      disabled={disabled}
      inputProps={inputProps}
      value={text ?? value ?? ""}
      onChange={(e) => setText(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => e.key === "Enter" && (e.target as HTMLInputElement).blur()}
      sx={NUM_FIELD_SX}
    />
  );
};

// mode가 2지선다 select면 on/off 스위치로 헤더에 뺀다 ([on, off] 순서)
// AI 초기설정에 limit이 없는 옵션(JSON으로 새로 추가한 이름 등)도 mode가 enabled/disabled면 스위치로 보여준다
const getModeOpts = (limits: Record<string, any>, mode: unknown): [string, string] | null => {
  const spec = limits.mode;
  if (Array.isArray(spec) && spec[0] === "select" && Array.isArray(spec[2]) && spec[2].length === 2)
    return spec[2] as [string, string];
  return mode === "enabled" || mode === "disabled" ? ["enabled", "disabled"] : null;
};

// connection은 파이프라인 연결 정보라 UI에서는 숨긴다 (JSON 모드에서 편집)
const HIDDEN_PARAMS = ["connection"];

const FieldRow = ({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 2,
      py: 0.75,
      px: 1.5,
      borderBottom: "1px solid var(--dk-border, #eee)",
      "&:last-child": { borderBottom: "none" },
    }}
  >
    <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, minWidth: 0 }}>
      <Typography variant="body2" fontWeight={500} noWrap>
        {label}
      </Typography>
      {hint && (
        <Typography variant="caption" color="text.secondary" noWrap>
          {hint}
        </Typography>
      )}
    </Box>
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>{children}</Box>
  </Box>
);

// JSON 편집기: 옵션 한 줄씩(줄바꿈 없이 가로 스크롤), 맨 앞 옵션 키는 하이라이트
// textarea 글자는 투명하게 두고 같은 위치의 <pre>에 색을 입혀 보여준다 (에디터 라이브러리 없이)
const EDITOR_TEXT_SX = {
  m: 0,
  p: 1.5,
  // textarea와 <pre>의 글자 폭이 다르면 선택 블럭이 어긋난다 → 폰트 속성을 전부 고정
  fontFamily: "Consolas, Menlo, 'DejaVu Sans Mono', monospace",
  fontSize: 12,
  fontWeight: 400,
  letterSpacing: 0,
  fontVariantLigatures: "none",
  tabSize: 2,
  lineHeight: "20px",
  whiteSpace: "pre",
  border: "none",
  boxSizing: "border-box",
  // 전역 CSS `* { font-family: NotoSansKR }`가 <pre> 안의 div/span에 직접 걸려 폰트가 달라지는 것 방지
  "& *": { fontFamily: "inherit", fontSize: "inherit", fontWeight: "inherit", letterSpacing: "inherit" },
} as const;
const KEY_RE = /^(\s*)("(?:[^"\\]|\\.)*")(\s*:)/;

export function JsonOptionEditor({
  value,
  onChange,
  error,
}: {
  value: string;
  onChange: (text: string) => void;
  error?: string | null;
}) {
  const lines = value.split("\n");
  return (
    <Box>
      {error && (
        <Typography variant="caption" color="error" sx={{ display: "block", mb: 0.5 }}>
          {error}
        </Typography>
      )}
      {/* 가로 스크롤은 바깥 박스 하나만. textarea와 <pre>가 각자 스크롤하면 둘이 어긋나 선택 블럭이 밀려 보인다 */}
      <Box
        sx={{
          overflowX: "auto",
          border: "1px solid",
          borderColor: error ? "error.main" : "var(--dk-border, #ccc)",
          borderRadius: 1,
          background: "var(--dk-surface, #fff)",
        }}
      >
        {/* 가장 긴 줄 폭만큼 넓어지고, textarea는 이 크기에 딱 맞게 겹친다 */}
        <Box sx={{ position: "relative", width: "max-content", minWidth: "100%" }}>
          <Box component="pre" aria-hidden sx={{ ...EDITOR_TEXT_SX, color: "text.primary" }}>
            {lines.map((line, i) => {
              const m = line.match(KEY_RE);
              return (
                <div key={i}>
                  {m ? (
                    <>
                      {m[1]}
                      <Box
                        component="span"
                        // 굵게(bold)는 글자 폭이 바뀌어 textarea와 어긋나므로 색/배경만
                        sx={{ color: "primary.main", background: "rgba(25,118,210,0.12)", borderRadius: 0.5 }}
                      >
                        {m[2]}
                      </Box>
                      {line.slice(m[0].length - m[3].length)}
                    </>
                  ) : (
                    line || " "
                  )}
                </div>
              );
            })}
          </Box>
          <Box
            component="textarea"
            value={value}
            wrap="off"
            spellCheck={false}
            rows={lines.length}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => onChange(e.target.value)}
            // 입력 직후 <pre>가 다시 그려지기 전 잠깐 생기는 내부 스크롤도 0으로 고정
            onScroll={(e: React.UIEvent<HTMLTextAreaElement>) => {
              e.currentTarget.scrollLeft = 0;
              e.currentTarget.scrollTop = 0;
            }}
            sx={{
              ...EDITOR_TEXT_SX,
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              resize: "none",
              outline: "none",
              overflow: "hidden",
              color: "transparent",
              caretColor: "var(--dk-text, #000)",
              background: "transparent",
            }}
          />
        </Box>
      </Box>
    </Box>
  );
}

// memo: DefectPreview의 다른 상태(미리보기 이미지, 학습 설정 등)가 바뀔 때마다 패널 전체를 다시 그리지 않게
export default memo(function DefectOptionPanel({
  options,
  initialOptions,
  onChange,
  onReorder,
  onSolo,
}: DefectOptionPanelProps) {
  // limit이 없는 para(JSON으로 추가한 값 등): 값 모양으로 입력칸을 고른다. 범위 제한은 없음
  const renderFreeField = (paramKey: string, value: any, disabled: boolean, update: (v: any) => void) => {
    const num = (v: any, onInput: (n: number) => void) => (
      <NumField value={v} disabled={disabled} onCommit={(raw) => onInput(Number(raw))} />
    );
    if (typeof value === "number")
      return (
        <FieldRow key={paramKey} label={paramKey}>
          {num(value, update)}
        </FieldRow>
      );
    if (Array.isArray(value) && value.length === 2 && value.every((v) => typeof v === "number"))
      return (
        <FieldRow key={paramKey} label={paramKey}>
          {num(value[0], (n) => update([n, value[1]]))}
          <Typography variant="body2" color="text.secondary">
            ~
          </Typography>
          {num(value[1], (n) => update([value[0], n]))}
        </FieldRow>
      );
    if (typeof value === "string")
      return (
        <FieldRow key={paramKey} label={paramKey}>
          <TextField
            size="small"
            disabled={disabled}
            value={value}
            onChange={(e) => update(e.target.value)}
            sx={{ minWidth: 160 }}
          />
        </FieldRow>
      );
    // 객체/긴 배열 등은 UI에서 편집하기 어려워 값만 보여준다
    return (
      <FieldRow key={paramKey} label={paramKey} hint={t("EditInJsonMsg", "JSON 모드에서 편집")}>
        <Typography variant="body2" sx={{ fontFamily: "monospace", maxWidth: 240 }} noWrap>
          {JSON.stringify(value)}
        </Typography>
      </FieldRow>
    );
  };

  const renderField = (optionKey: string, paramKey: string, value: any, spec: any, disabled: boolean) => {
    const update = (v: any) => onChange(optionKey, paramKey, v);
    if (!spec) return renderFreeField(paramKey, value, disabled, update);
    const [kind, dtype, rangeOrOpts] = spec as [string, string, any[]];
    const isInt = dtype === "int";

    if (kind === "select") {
      return (
        <FieldRow key={paramKey} label={paramKey}>
          <TextField
            select
            size="small"
            disabled={disabled}
            value={value ?? ""}
            onChange={(e) => update(e.target.value)}
            sx={{ minWidth: 140 }}
          >
            {(rangeOrOpts ?? []).map((opt) => (
              <MenuItem key={String(opt)} value={opt}>
                {String(opt)}
              </MenuItem>
            ))}
          </TextField>
        </FieldRow>
      );
    }

    if (kind === "range" || (kind === "value" && dtype !== "string")) {
      const [min = 0, max = 1] = (rangeOrOpts ?? []) as number[];
      const step = isInt ? 1 : 0.01;
      const numInput = (v: any, onInput: (raw: string) => void) => (
        <NumField value={v} disabled={disabled} inputProps={{ step, min, max }} onCommit={onInput} />
      );

      if (kind === "value") {
        return (
          <FieldRow key={paramKey} label={paramKey} hint={`${min}~${max}`}>
            {numInput(value, (raw) => update(clamp(raw, min, max, isInt)))}
          </FieldRow>
        );
      }

      const pair: [number, number] = Array.isArray(value) ? [value[0], value[1]] : [value ?? min, value ?? max];
      // low <= high 유지
      const updatePair = (idx: 0 | 1, raw: string) => {
        const next: [number, number] = [...pair];
        next[idx] = clamp(raw, min, max, isInt);
        update(idx === 0 ? [Math.min(next[0], next[1]), next[1]] : [next[0], Math.max(next[0], next[1])]);
      };
      return (
        <FieldRow key={paramKey} label={paramKey} hint={`${min}~${max}`}>
          {numInput(pair[0], (raw) => updatePair(0, raw))}
          <Typography variant="body2" color="text.secondary">
            ~
          </Typography>
          {numInput(pair[1], (raw) => updatePair(1, raw))}
        </FieldRow>
      );
    }

    return (
      <FieldRow key={paramKey} label={paramKey}>
        <TextField
          size="small"
          disabled={disabled}
          value={value ?? ""}
          onChange={(e) => update(e.target.value)}
          sx={{ minWidth: 160 }}
        />
      </FieldRow>
    );
  };

  // 드래그로 옵션 순서 변경 → 순서 = 파이프라인 연결 순서 (connection은 Save 때 다시 계산)
  // 펼침 상태는 직접 관리: 드래그 중에는 전부 접어서 헤더만 움직이게, 놓으면 원래 펼친 옵션은 다시 펼친다
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [dragging, setDragging] = useState(false);

  // 드래그 라이브러리가 항목 크기를 재기 직전에 접어야 자리 계산이 맞는다 → flushSync로 즉시 반영
  const handleBeforeCapture = () => flushSync(() => setDragging(true));
  const handleDragEnd = (result: DropResult) => {
    setDragging(false);
    if (result.destination && result.destination.index !== result.source.index)
      onReorder?.(result.source.index, result.destination.index);
  };

  return (
    <DragDropContext onBeforeCapture={handleBeforeCapture} onDragEnd={handleDragEnd}>
      <Droppable droppableId="defect-options" isDropDisabled={!onReorder}>
        {(dropProvided) => (
          <Box ref={dropProvided.innerRef} {...dropProvided.droppableProps}>
            {Object.entries(options).map(([optionKey, option], index) => {
              const limits = resolveParamLimits(option, initialOptions);
              const modeOpts = getModeOpts(limits, option.para?.mode);
              const isOn = !modeOpts || String(option.para.mode) !== String(modeOpts[1]);
              // limit에 없는 para도 보여준다 (JSON으로 추가한 para)
              const fieldKeys = Object.keys(option.para ?? {}).filter(
                (k) => !HIDDEN_PARAMS.includes(k) && !(modeOpts && k === "mode"),
              );

              return (
                <Draggable key={optionKey} draggableId={optionKey} index={index} isDragDisabled={!onReorder}>
                  {(dragProvided, snapshot) => (
                    <Accordion
                      ref={dragProvided.innerRef}
                      {...dragProvided.draggableProps}
                      disableGutters
                      expanded={!dragging && !!expanded[optionKey]}
                      onChange={(_, isOpen) => setExpanded((prev) => ({ ...prev, [optionKey]: isOpen }))}
                      // 기본값은 접혀 있어도 필드를 전부 그려둔다 → 옵션 수 × 필드 수만큼 입력칸이 한꺼번에 생겨 패널 표시가 느려진다
                      // 드래그 시작 때는 접힘 애니메이션 없이 바로 접는다 (크기 측정과 어긋나지 않게)
                      TransitionProps={{ unmountOnExit: true, timeout: dragging ? 0 : "auto" }}
                      sx={{
                        mb: 1,
                        borderRadius: 1,
                        boxShadow: snapshot.isDragging ? 4 : "none",
                        "&:before": { display: "none" },
                      }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        title={onSolo ? t("SoloOptionMsg", "우클릭: 이 옵션만 켜기") : undefined}
                        onContextMenu={
                          onSolo &&
                          ((e: React.MouseEvent) => {
                            e.preventDefault();
                            onSolo(optionKey);
                          })
                        }
                        sx={{
                          minHeight: 40,
                          "& .MuiAccordionSummary-content": { my: 0, alignItems: "center", gap: 1 },
                        }}
                      >
                        {onReorder && (
                          // 손잡이로만 드래그 (요약 줄을 누르면 펼치기와 겹치지 않게)
                          <Box
                            {...dragProvided.dragHandleProps}
                            onClick={(e) => e.stopPropagation()}
                            sx={{ display: "flex", color: "text.secondary", cursor: "grab" }}
                          >
                            <DragIndicatorIcon fontSize="small" />
                          </Box>
                        )}
                        {modeOpts && (
                          <Switch
                            size="small"
                            checked={isOn}
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) => onChange(optionKey, "mode", e.target.checked ? modeOpts[0] : modeOpts[1])}
                          />
                        )}
                        <Typography fontWeight={600} sx={{ opacity: isOn ? 1 : 0.5 }}>
                          {optionKey}
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails sx={{ p: 0, borderTop: "1px solid var(--dk-border, #eee)" }}>
                        {fieldKeys.length ? (
                          fieldKeys.map((paramKey) =>
                            renderField(optionKey, paramKey, option.para[paramKey], limits[paramKey], !isOn),
                          )
                        ) : (
                          <Typography variant="caption" color="text.secondary" sx={{ display: "block", p: 1.5 }}>
                            -
                          </Typography>
                        )}
                      </AccordionDetails>
                    </Accordion>
                  )}
                </Draggable>
              );
            })}
            {dropProvided.placeholder}
          </Box>
        )}
      </Droppable>
    </DragDropContext>
  );
});
